<template>
  <div class="app-image-loader" :class="{ 'app-image-loader--empty': !hasSource }">
    <div v-if="showSpinnerState" class="app-image-loader__spinner">
      <i class="fa fa-circle-o-notch fa-spin" aria-hidden="true"></i>
    </div>

    <img
      v-if="hasSource"
      v-show="!isFailed"
      :src="normalizedSrc"
      :alt="alt"
      class="app-image-loader__image"
      :class="imageClass"
      @load="handleLoad"
      @error="handleError"
    />

    <div v-if="!hasSource || isFailed" class="app-image-loader__fallback">
      <slot name="fallback"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppImageLoader',
  emits: ['load', 'error'],
  props: {
    src: {
      type: String,
      default: '',
    },
    alt: {
      type: String,
      default: '',
    },
    fit: {
      type: String,
      default: 'cover',
      validator(value) {
        const normalized = String(value || '').trim().toLowerCase();
        return normalized === 'cover' || normalized === 'contain';
      },
    },
    showSpinner: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isLoaded: false,
      isFailed: false,
    };
  },
  computed: {
    normalizedSrc() {
      return String(this.src || '').trim();
    },
    hasSource() {
      return this.normalizedSrc !== '';
    },
    showSpinnerState() {
      return this.showSpinner && this.hasSource && !this.isLoaded && !this.isFailed;
    },
    imageClass() {
      const fit = String(this.fit || 'cover').trim().toLowerCase();

      return {
        'app-image-loader__image--loaded': this.isLoaded,
        'app-image-loader__image--contain': fit === 'contain',
        'app-image-loader__image--cover': fit !== 'contain',
      };
    },
  },
  watch: {
    normalizedSrc: {
      immediate: true,
      handler() {
        this.isLoaded = false;
        this.isFailed = false;
      },
    },
  },
  methods: {
    handleLoad() {
      this.isLoaded = true;
      this.isFailed = false;
      this.$emit('load');
    },
    handleError() {
      this.isLoaded = false;
      this.isFailed = true;
      this.$emit('error');
    },
  },
};
</script>

<style scoped>
.app-image-loader {
  position: relative;
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.app-image-loader__spinner {
  position: absolute;
  inset: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #1d4ed8;
  background: rgba(248, 250, 252, 0.72);
  z-index: 1;
}

.app-image-loader__image {
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 150ms ease;
}

.app-image-loader__image--loaded {
  opacity: 1;
}

.app-image-loader__image--contain {
  object-fit: contain;
}

.app-image-loader__image--cover {
  object-fit: cover;
}

.app-image-loader__fallback {
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
